import React, { useState } from 'react';
import { BACKEND_URL } from '../config';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import {
  Plus, Trash2, Calendar, Clock, MapPin, Users, CheckCircle, UserPlus, UserMinus,
  FileText, ChevronDown, ChevronUp, Megaphone
} from 'lucide-react';
import { toast } from 'sonner';

const API = BACKEND_URL;

const TYPE_LABELS = {
  world_cafe: 'World Café', focus_group: 'Focus Group', open_space: 'Open Space',
  design_sprint: 'Design Sprint', public_consultation: 'Public Consultation', other: 'Other',
};

const TYPE_COLORS = {
  world_cafe: '#F59E0B', focus_group: '#3B82F6', open_space: '#10B981',
  design_sprint: '#8B5CF6', public_consultation: '#EF4444', other: '#6B7280',
};

const STATUS_CONFIG = {
  upcoming: { label: 'Upcoming', bg: 'bg-blue-100 text-blue-700' },
  in_progress: { label: 'In Progress', bg: 'bg-amber-100 text-amber-700' },
  completed: { label: 'Completed', bg: 'bg-green-100 text-green-700' },
  cancelled: { label: 'Cancelled', bg: 'bg-gray-100 text-gray-500' },
};

const LOC_LABELS = { in_person: 'In-Person', online: 'Online', hybrid: 'Hybrid' };

const PDWorkshops = ({ ideaId, workshops: initialWorkshops, canManage, userId }) => {
  const [workshops, setWorkshops] = useState(initialWorkshops || []);
  const [showCreate, setShowCreate] = useState(false);
  const [showOutcomes, setShowOutcomes] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [newWs, setNewWs] = useState({
    title: '', description: '', workshop_type: 'world_cafe', date: '', time_start: '', time_end: '',
    location: '', location_type: 'in_person', max_participants: 30, facilitator: '', agenda: [''],
  });
  const [outcomes, setOutcomes] = useState({ summary: '', key_decisions: [''], action_items: [''], participant_feedback: '' });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchWorkshops = async () => {
    try {
      const res = await axios.get(`${API}/api/participatory-design/ideas/${ideaId}/workshops`, { headers });
      setWorkshops(res.data);
    } catch { /* ignore */ }
  };

  const handleCreate = async () => {
    if (!newWs.title || !newWs.date) { toast.error('Title and date required'); return; }
    try {
      await axios.post(`${API}/api/participatory-design/ideas/${ideaId}/workshops`, {
        ...newWs, agenda: newWs.agenda.filter(a => a.trim()), max_participants: parseInt(newWs.max_participants) || 0,
      }, { headers });
      toast.success('Workshop created');
      setShowCreate(false);
      setNewWs({ title: '', description: '', workshop_type: 'world_cafe', date: '', time_start: '', time_end: '', location: '', location_type: 'in_person', max_participants: 30, facilitator: '', agenda: [''] });
      fetchWorkshops();
    } catch { toast.error('Failed to create'); }
  };

  const handleRsvp = async (workshopId, action) => {
    try {
      await axios.post(`${API}/api/participatory-design/workshops/${workshopId}/rsvp`, { action }, { headers });
      toast.success(action === 'join' ? 'RSVP confirmed' : 'RSVP cancelled');
      fetchWorkshops();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleSaveOutcomes = async (workshopId) => {
    try {
      await axios.post(`${API}/api/participatory-design/workshops/${workshopId}/outcomes`, {
        ...outcomes, key_decisions: outcomes.key_decisions.filter(d => d.trim()), action_items: outcomes.action_items.filter(a => a.trim()),
      }, { headers });
      toast.success('Outcomes saved');
      setShowOutcomes(null);
      fetchWorkshops();
    } catch { toast.error('Failed to save outcomes'); }
  };

  const handleDelete = async (workshopId) => {
    try {
      await axios.delete(`${API}/api/participatory-design/workshops/${workshopId}`, { headers });
      toast.success('Workshop deleted');
      fetchWorkshops();
    } catch { toast.error('Failed to delete'); }
  };

  const addAgendaItem = () => setNewWs(p => ({ ...p, agenda: [...p.agenda, ''] }));
  const updateAgenda = (idx, val) => setNewWs(p => { const a = [...p.agenda]; a[idx] = val; return { ...p, agenda: a }; });
  const removeAgenda = (idx) => setNewWs(p => ({ ...p, agenda: p.agenda.filter((_, i) => i !== idx) }));

  const addOutcomeItem = (field) => setOutcomes(p => ({ ...p, [field]: [...p[field], ''] }));
  const updateOutcomeItem = (field, idx, val) => setOutcomes(p => { const a = [...p[field]]; a[idx] = val; return { ...p, [field]: a }; });
  const removeOutcomeItem = (field, idx) => setOutcomes(p => ({ ...p, [field]: p[field].filter((_, i) => i !== idx) }));

  const upcoming = workshops.filter(w => w.status === 'upcoming');
  const past = workshops.filter(w => w.status !== 'upcoming');

  return (
    <div className="space-y-4" data-testid="workshops-section">
      {canManage && (
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-500">{workshops.length} workshop{workshops.length !== 1 ? 's' : ''}</span>
          <Button size="sm" variant="outline" onClick={() => setShowCreate(true)} data-testid="create-workshop-btn"><Plus className="h-3.5 w-3.5 mr-1" /> Schedule Workshop</Button>
        </div>
      )}

      {workshops.length === 0 ? (
        <Card className="border-dashed"><CardContent className="py-10 text-center text-sm text-gray-500">
          <Calendar className="h-8 w-8 mx-auto mb-2 text-gray-300" />
          No workshops scheduled yet. {canManage && 'Create one to organize co-design events.'}
        </CardContent></Card>
      ) : (
        <>
          {/* Upcoming */}
          {upcoming.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Upcoming</p>
              <div className="space-y-2">
                {upcoming.map(ws => renderWorkshopCard(ws, true))}
              </div>
            </div>
          )}
          {/* Past / Completed */}
          {past.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 mt-4">Past Events</p>
              <div className="space-y-2">
                {past.map(ws => renderWorkshopCard(ws, false))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Create Workshop Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Schedule Workshop / Event</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            <div><Label>Title *</Label><Input value={newWs.title} onChange={e => setNewWs({ ...newWs, title: e.target.value })} placeholder="e.g., Community Needs Mapping" data-testid="ws-title-input" /></div>
            <div><Label>Description</Label><Textarea value={newWs.description} onChange={e => setNewWs({ ...newWs, description: e.target.value })} rows={2} placeholder="Workshop purpose and goals..." /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Type</Label>
                <Select value={newWs.workshop_type} onValueChange={v => setNewWs({ ...newWs, workshop_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Format</Label>
                <Select value={newWs.location_type} onValueChange={v => setNewWs({ ...newWs, location_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(LOC_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Date *</Label><Input type="date" value={newWs.date} onChange={e => setNewWs({ ...newWs, date: e.target.value })} data-testid="ws-date-input" /></div>
              <div><Label>Start Time</Label><Input type="time" value={newWs.time_start} onChange={e => setNewWs({ ...newWs, time_start: e.target.value })} /></div>
              <div><Label>End Time</Label><Input type="time" value={newWs.time_end} onChange={e => setNewWs({ ...newWs, time_end: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Location</Label><Input value={newWs.location} onChange={e => setNewWs({ ...newWs, location: e.target.value })} placeholder="Venue or URL" /></div>
              <div><Label>Max Participants</Label><Input type="number" value={newWs.max_participants} onChange={e => setNewWs({ ...newWs, max_participants: e.target.value })} /></div>
            </div>
            <div><Label>Facilitator</Label><Input value={newWs.facilitator} onChange={e => setNewWs({ ...newWs, facilitator: e.target.value })} placeholder="Lead facilitator name" /></div>
            <div>
              <div className="flex items-center justify-between mb-1"><Label>Agenda</Label><Button size="sm" variant="ghost" onClick={addAgendaItem}><Plus className="h-3 w-3 mr-0.5" /> Item</Button></div>
              {newWs.agenda.map((a, i) => (
                <div key={i} className="flex gap-1 mb-1">
                  <Input value={a} onChange={e => updateAgenda(i, e.target.value)} placeholder={`Agenda item ${i + 1}`} className="h-7 text-xs" />
                  {newWs.agenda.length > 1 && <Button size="sm" variant="ghost" className="text-red-400 h-7" onClick={() => removeAgenda(i)}><Trash2 className="h-2.5 w-2.5" /></Button>}
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-2"><Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button><Button onClick={handleCreate} className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-ws-btn">Schedule</Button></div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Outcomes Dialog */}
      <Dialog open={!!showOutcomes} onOpenChange={() => setShowOutcomes(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Workshop Outcomes</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            <div><Label>Summary *</Label><Textarea value={outcomes.summary} onChange={e => setOutcomes({ ...outcomes, summary: e.target.value })} rows={3} placeholder="What were the main outcomes?" data-testid="outcomes-summary" /></div>
            <div>
              <div className="flex items-center justify-between mb-1"><Label>Key Decisions</Label><Button size="sm" variant="ghost" onClick={() => addOutcomeItem('key_decisions')}><Plus className="h-3 w-3" /></Button></div>
              {outcomes.key_decisions.map((d, i) => (
                <div key={i} className="flex gap-1 mb-1">
                  <Input value={d} onChange={e => updateOutcomeItem('key_decisions', i, e.target.value)} placeholder="Decision..." className="h-7 text-xs" />
                  {outcomes.key_decisions.length > 1 && <Button size="sm" variant="ghost" className="text-red-400 h-7" onClick={() => removeOutcomeItem('key_decisions', i)}><Trash2 className="h-2.5 w-2.5" /></Button>}
                </div>
              ))}
            </div>
            <div>
              <div className="flex items-center justify-between mb-1"><Label>Action Items</Label><Button size="sm" variant="ghost" onClick={() => addOutcomeItem('action_items')}><Plus className="h-3 w-3" /></Button></div>
              {outcomes.action_items.map((a, i) => (
                <div key={i} className="flex gap-1 mb-1">
                  <Input value={a} onChange={e => updateOutcomeItem('action_items', i, e.target.value)} placeholder="Action item..." className="h-7 text-xs" />
                  {outcomes.action_items.length > 1 && <Button size="sm" variant="ghost" className="text-red-400 h-7" onClick={() => removeOutcomeItem('action_items', i)}><Trash2 className="h-2.5 w-2.5" /></Button>}
                </div>
              ))}
            </div>
            <div><Label>Participant Feedback</Label><Textarea value={outcomes.participant_feedback} onChange={e => setOutcomes({ ...outcomes, participant_feedback: e.target.value })} rows={2} placeholder="General feedback from participants..." /></div>
            <div className="flex justify-end gap-2 pt-2"><Button variant="outline" onClick={() => setShowOutcomes(null)}>Cancel</Button><Button onClick={() => handleSaveOutcomes(showOutcomes)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-outcomes-btn">Save Outcomes</Button></div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );

  function renderWorkshopCard(ws, isUpcoming) {
    const expanded = expandedId === ws.id;
    const typeColor = TYPE_COLORS[ws.workshop_type] || '#6B7280';
    const sc = STATUS_CONFIG[ws.status] || STATUS_CONFIG.upcoming;
    const userRsvp = ws.user_rsvp || (ws.rsvps || []).find(r => r.user_id === userId);
    const spotsLeft = ws.max_participants > 0 ? ws.max_participants - (ws.rsvp_count || 0) : null;

    return (
      <Card key={ws.id} data-testid={`ws-${ws.id}`} className="border" style={{ borderLeftColor: typeColor, borderLeftWidth: 4 }}>
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <Badge className="text-xs" style={{ backgroundColor: typeColor + '20', color: typeColor }}>{TYPE_LABELS[ws.workshop_type]}</Badge>
                <Badge className={`text-xs ${sc.bg}`}>{sc.label}</Badge>
                <Badge variant="outline" className="text-xs">{LOC_LABELS[ws.location_type]}</Badge>
              </div>
              <h4 className="text-sm font-semibold text-gray-900">{ws.title}</h4>
              <div className="flex flex-wrap gap-3 mt-1 text-xs text-gray-500">
                <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{new Date(ws.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}</span>
                {ws.time_start && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{ws.time_start}{ws.time_end ? ` - ${ws.time_end}` : ''}</span>}
                {ws.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{ws.location}</span>}
                <span className="flex items-center gap-1"><Users className="h-3 w-3" />{ws.rsvp_count || 0}{ws.max_participants > 0 ? `/${ws.max_participants}` : ''} RSVPs</span>
                {ws.attended_count > 0 && <span className="flex items-center gap-1"><CheckCircle className="h-3 w-3 text-green-500" />{ws.attended_count} attended</span>}
              </div>
            </div>
            <div className="flex gap-1 shrink-0 items-center">
              {isUpcoming && !userRsvp && (
                <Button size="sm" className="bg-green-700 hover:bg-green-800 text-xs h-7" onClick={() => handleRsvp(ws.id, 'join')} data-testid={`rsvp-join-${ws.id}`}>
                  <UserPlus className="h-3 w-3 mr-0.5" /> RSVP
                </Button>
              )}
              {isUpcoming && userRsvp && (
                <Button size="sm" variant="outline" className="text-xs h-7 text-red-600 border-red-200" onClick={() => handleRsvp(ws.id, 'cancel')} data-testid={`rsvp-cancel-${ws.id}`}>
                  <UserMinus className="h-3 w-3 mr-0.5" /> Cancel
                </Button>
              )}
              {spotsLeft !== null && spotsLeft <= 5 && spotsLeft > 0 && isUpcoming && <Badge className="text-xs bg-amber-100 text-amber-700">{spotsLeft} left</Badge>}
              <Button size="sm" variant="ghost" className="h-7" onClick={() => setExpandedId(expanded ? null : ws.id)}>
                {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              </Button>
            </div>
          </div>

          {expanded && (
            <div className="mt-3 pt-3 border-t space-y-3">
              {ws.description && <p className="text-xs text-gray-600">{ws.description}</p>}
              {ws.facilitator && <p className="text-xs text-gray-500">Facilitator: <strong>{ws.facilitator}</strong></p>}

              {/* Agenda */}
              {(ws.agenda || []).length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-700 mb-1">Agenda</p>
                  <ol className="list-decimal ml-5 space-y-0.5">
                    {ws.agenda.map((a, i) => <li key={i} className="text-xs text-gray-600">{a}</li>)}
                  </ol>
                </div>
              )}

              {/* RSVPs */}
              {(ws.rsvps || []).length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-700 mb-1">Participants ({ws.rsvps.length})</p>
                  <div className="flex flex-wrap gap-1">
                    {ws.rsvps.map((r, i) => (
                      <Badge key={i} variant="outline" className={`text-xs ${r.attended ? 'bg-green-50 border-green-200 text-green-700' : ''}`}>
                        {r.username} {r.attended && <CheckCircle className="h-2.5 w-2.5 ml-0.5" />}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Outcomes */}
              {ws.outcomes ? (
                <Card className="bg-green-50/50 border-green-100">
                  <CardContent className="p-3">
                    <p className="text-xs font-semibold text-green-700 mb-1 flex items-center gap-1"><FileText className="h-3.5 w-3.5" /> Outcomes</p>
                    <p className="text-xs text-gray-700 mb-2">{ws.outcomes.summary}</p>
                    {(ws.outcomes.key_decisions || []).length > 0 && (
                      <div className="mb-1.5">
                        <p className="text-xs font-medium text-gray-500">Key Decisions:</p>
                        <ul className="list-disc ml-4">{ws.outcomes.key_decisions.map((d, i) => <li key={i} className="text-xs text-gray-600">{d}</li>)}</ul>
                      </div>
                    )}
                    {(ws.outcomes.action_items || []).length > 0 && (
                      <div className="mb-1.5">
                        <p className="text-xs font-medium text-gray-500">Action Items:</p>
                        <ul className="list-disc ml-4">{ws.outcomes.action_items.map((a, i) => <li key={i} className="text-xs text-gray-600">{a}</li>)}</ul>
                      </div>
                    )}
                    {ws.outcomes.participant_feedback && <p className="text-xs text-gray-500 italic mt-1">Feedback: "{ws.outcomes.participant_feedback}"</p>}
                  </CardContent>
                </Card>
              ) : canManage && ws.status !== 'upcoming' ? (
                <Button size="sm" variant="outline" className="text-xs" onClick={() => { setOutcomes({ summary: '', key_decisions: [''], action_items: [''], participant_feedback: '' }); setShowOutcomes(ws.id); }} data-testid={`add-outcomes-${ws.id}`}>
                  <FileText className="h-3 w-3 mr-1" /> Record Outcomes
                </Button>
              ) : null}

              {/* Admin actions */}
              {canManage && (
                <div className="flex gap-2 pt-1">
                  {ws.status === 'upcoming' && <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => handleDelete(ws.id)}><Trash2 className="h-3 w-3 mr-0.5" /> Delete</Button>}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }
};

export default PDWorkshops;
